import {
  Account,
  Address,
  ArgSerializer,
  EndpointDefinition,
  ITransactionOnNetwork,
  ResultsParser,
  Transaction,
  TransactionWatcher,
  ITransactionFetcher,
} from "@multiversx/sdk-core";
import ELROND from "@/constants/elrond";
import elrondApiHelper from "@/helpers/elrondApi";
import { NetworkConfig, ProxyNetworkProvider, TransactionOnNetwork } from "@multiversx/sdk-network-providers";
import { ExtensionProvider } from "@multiversx/sdk-extension-provider";
import { WalletConnectV2Provider } from "@multiversx/sdk-wallet-connect-provider";
import { HWProvider } from "@multiversx/sdk-hw-provider";
import { TransactionDecoder } from "@multiversx/sdk-transaction-decoder/lib/src/transaction.decoder";
import { WalletProvider } from "@multiversx/sdk-web-wallet-provider";
import { Interaction } from "@multiversx/sdk-core/out";
import storageHelper from "@/helpers/storage";
import { WebviewProvider } from "@multiversx/sdk-webview-provider/out";

export interface DecodedLoginTokenType {
  blockHash: string;
  extraInfo?: { timestamp: number };
  origin: string;
  ttl: number;
}

export interface DecodedNativeAuthTokenType extends DecodedLoginTokenType {
  address: string;
  body: string;
  signature: string;
}

export enum WALLET_LOGIN_TYPE {
  WEB_WALLET = "web_wallet",
  XALIAS = "xalias",
}

class ElrondHelper {
  provider: ExtensionProvider | WalletConnectV2Provider | HWProvider | WalletProvider | WebviewProvider;
  proxy: ProxyNetworkProvider;
  cachedProxy: ProxyNetworkProvider;
  networkConfig: NetworkConfig;
  transactionCache: {
    [hash: string]: TransactionOnNetwork;
  };

  constructor() {
    this.transactionCache = {};

    this.proxy = new ProxyNetworkProvider(ELROND.GATEWAY, { timeout: 90000 });
    this.cachedProxy = new ProxyNetworkProvider(ELROND.GATEWAY, { timeout: 90000 });
  }

  async setNetworkConfig() {
    this.networkConfig = await this.cachedProxy.getNetworkConfig();
  }

  async initializeMaiar(onClientLogin, onClientLogout) {
    await this.setNetworkConfig();

    this.provider = new WalletConnectV2Provider(
      {
        onClientLogin,
        onClientLogout,
        onClientEvent: () => {
        },
      },
      this.networkConfig.ChainID,
      ELROND.WALLET_CONNECT_WS,
      ELROND.WALLET_CONNECT_PROJECT_ID,
    );

    return await this.provider.init();
  }

  async initializeExtension(initialAddress) {
    if (!initialAddress) {
      this.provider = ExtensionProvider.getInstance();
    } else {
      this.provider = ExtensionProvider.getInstance();

      this.provider.setAddress(initialAddress);
    }

    await this.setNetworkConfig();

    return await this.provider.init();
  }

  async initializeLedger() {
    this.provider = new HWProvider();

    await this.setNetworkConfig();

    return await this.provider.init();
  }

  async initializeWebWallet(type: WALLET_LOGIN_TYPE) {
    this.provider = new WalletProvider(
      type === WALLET_LOGIN_TYPE.WEB_WALLET ? ELROND.WALLET_PROVIDER : ELROND.XALIAS_PROVIDER,
    );

    await this.setNetworkConfig();
  }

  async initializeWebView(account: DecodedNativeAuthTokenType, accessToken: string) {
    this.provider = WebviewProvider.getInstance();

    this.provider.setAccount({
      address: account.address,
      signature: account.signature,
      accessToken,
    });

    await this.setNetworkConfig();

    return await this.provider.init();
  }

  async login(redirect = "", token = "", addressIndex = null, isReload = false) {
    if (this.provider instanceof HWProvider) {
      if (isReload) {
        await this.provider.setAddressIndex(addressIndex);

        return "";
      }

      if (!token) {
        const { address } = await this.provider.login({
          addressIndex,
        });

        return address;
      }

      return await this.provider.tokenLogin({
        addressIndex,
        token: Buffer.from(`${ token }{}`),
      });
    }

    if (this.provider instanceof WalletConnectV2Provider) {
      const { uri, approval } = await this.provider.connect();

      const params: any = { approval };

      if (token) {
        params.token = token;
      }

      this.provider
        .login(params)
        .then((value) => console.log("successfully connected", value))
        .catch((e) => console.log("error while connecting...", e));

      return uri;
    }

    if (this.provider instanceof ExtensionProvider) {
      const { address } = await this.provider.login({
        callbackUrl: window.location.origin + encodeURIComponent(redirect),
        token,
      });

      return address;
    }

    return await this.provider.login({
      callbackUrl: window.location.origin + encodeURIComponent(redirect),
      token,
    });
  }

  async getAccount(address): Promise<Account> {
    return await elrondApiHelper.getAccount(address);
  }

  async logout(): Promise<void> {
    if (!this.provider) {
      return;
    }

    if (this.provider instanceof WalletProvider && storageHelper.getWalletLoginType() === WALLET_LOGIN_TYPE.XALIAS) {
      return;
    }

    if (!(this.provider instanceof WalletProvider) && !this.provider.isInitialized()) {
      return;
    }

    await this.provider.logout();
  }

  async getCurrentBlock(shard: number, force: boolean = false): Promise<number> {
    let result;

    if (force) {
      result = await this.proxy.doGetGeneric(`network/status/${ shard }`);
    } else {
      result = await this.cachedProxy.doGetGeneric(`network/status/${ shard }`);
    }

    return result.status.erd_nonce;
  }

  calculateMasks(numOfShards: number) {
    const n = Math.ceil(Math.log2(numOfShards));
    const mask1 = (1 << n) - 1;
    const mask2 = (1 << (n - 1)) - 1;
    return [mask1, mask2];
  }

  // TODO: Fetch total shards from api
  getAddressShard(addressStr: string, totalShards: number = 3): number {
    const address = Address.fromString(addressStr);
    const [maskHigh, maskLow] = this.calculateMasks(totalShards);
    const pubKey = address.pubkey();
    const lastByteOfPubKey = pubKey[31];

    if (this.isAddressOfMetachain(pubKey)) {
      return 4294967295;
    }

    let shard = lastByteOfPubKey & maskHigh;

    if (shard > totalShards - 1) {
      shard = lastByteOfPubKey & maskLow;
    }

    return shard;
  }

  isAddressOfMetachain(pubKey: Buffer): boolean {
    // prettier-ignore
    const metachainPrefix = Buffer.from([
      0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ]);
    const pubKeyPrefix = pubKey.slice(0, metachainPrefix.length);

    if (pubKeyPrefix.toString("hex") === metachainPrefix.toString("hex")) {
      return true;
    }

    const zeroAddress = Buffer.alloc(32).fill(0);

    if (pubKey.toString("hex") === zeroAddress.toString("hex")) {
      return true;
    }

    return false;
  }

  async buildAndSendInteraction(interaction: Interaction, account: Account): Promise<Transaction | null> {
    interaction.withNonce(account.nonce).withSender(account.address).withChainID(elrondHelper.networkConfig.ChainID);

    const transaction = interaction.buildTransaction();

    return await this.sendTransaction(transaction, account);
  }

  async sendTransaction(transaction: Transaction, account: Account): Promise<Transaction | null> {
    const { default: store, BASE_MUTATIONS } = await import("@/store");

    store.commit(BASE_MUTATIONS.SIGN_TRANSACTION_TOAST, true);

    try {
      transaction.setNonce(account.nonce);
      transaction.setSender(account.address);
      transaction.setChainID(elrondHelper.networkConfig.ChainID);

      if (this.provider instanceof WalletProvider) {
        await this.provider.signTransaction(transaction, {
          callbackUrl: encodeURIComponent(window.location.href),
        });

        return transaction;
      }

      // TODO: Implement proper guardian support for Ledger and test that Guardian signing works properly
      // @ts-ignore
      transaction = await this.provider.signTransaction(transaction);
      await this.proxy.sendTransaction(transaction);

      account.incrementNonce();

      return transaction;
    } catch (e) {
      console.error(e);

      return null;
    } finally {
      store.commit(BASE_MUTATIONS.SIGN_TRANSACTION_TOAST, null);
    }
  }

  async sendTransactions(
    transactions: Transaction[],
    account: Account,
    areSigned = false,
  ): Promise<Transaction[] | null> {
    const { default: store, BASE_MUTATIONS } = await import("@/store");

    store.commit(BASE_MUTATIONS.SIGN_TRANSACTION_TOAST, true);

    try {
      if (!areSigned) {
        for (const transaction of transactions) {
          transaction.setNonce(account.getNonceThenIncrement());
          transaction.setSender(account.address);
          transaction.setChainID(elrondHelper.networkConfig.ChainID);
        }

        if (this.provider instanceof WalletProvider) {
          // @ts-ignore
          await this.provider.signTransactions(transactions, {
            callbackUrl: encodeURIComponent(window.location.href),
          });

          return [];
        }

        // TODO: Implement proper guardian support for Ledger and test that Guardian signing works properly
        // @ts-ignore
        transactions = await this.provider.signTransactions(transactions);
      }

      let lastNonce;
      for (const transaction of transactions) {
        await this.proxy.sendTransaction(transaction);

        lastNonce = transaction.getNonce();
      }

      if (lastNonce) {
        account.update({ nonce: lastNonce, balance: account.balance });
        account.incrementNonce();
      }

      return transactions;
    } catch (e) {
      console.error(e);

      return null;
    } finally {
      store.commit(BASE_MUTATIONS.SIGN_TRANSACTION_TOAST, null);
    }
  }

  getTransactionStatus(
    transaction?: TransactionOnNetwork,
    allowPending: boolean = false,
    returnTransaction: boolean = false,
  ): boolean | string {
    if (!transaction) {
      console.warn("getTransactionStatus called with null transaction");
      return false;
    }

    // Check transaction data to determine transaction type
    const txData = transaction.data?.toString().toLowerCase() || "";
    const isEsdtTransaction = txData.includes("esdt");
    const isNutsRouletteTransaction = txData.includes("nuts_roulette");

    console.log("Checking transaction status for:", {
      isEsdtTransaction,
      isNutsRouletteTransaction,
      status: transaction.status.toString(),
    });

    // For ESDT or NutsRoulette transactions that are successful or pending, return immediately
    if ((isEsdtTransaction || isNutsRouletteTransaction) && (transaction.status.isSuccessful() || (allowPending && transaction.status.isPending()))) {
      console.log("ESDT or NutsRoulette transaction is successful or pending");
      return true;
    }

    // For transactions without contract results, rely on transaction status
    if (!transaction.contractResults) {
      console.log("Transaction has no contract results, using transaction status");
      return transaction.status.isSuccessful() || (allowPending && transaction.status.isPending());
    }

    try {
      // Check transaction data to determine transaction type
      const txData = transaction.data?.toString().toLowerCase() || "";
      const isEsdtTransaction = txData.includes("esdt");
      const isNutsRouletteTransaction = txData.includes("nuts_roulette");

      console.log("Checking transaction status for:", {
        isEsdtTransaction,
        isNutsRouletteTransaction,
        status: transaction.status.toString(),
      });

      // For ESDT or NutsRoulette transactions that are successful, we can return true
      // without trying to parse contract results
      if ((isEsdtTransaction || isNutsRouletteTransaction) && transaction.status.isSuccessful()) {
        console.log("ESDT or NutsRoulette transaction is successful");
        return true;
      }

      // For transactions with contract results, parse them
      const resultsParser = new ResultsParser();
      // Cast to ITransactionOnNetwork to satisfy type requirements
      const result = resultsParser.parseUntypedOutcome(transaction as unknown as ITransactionOnNetwork);

      console.log("Parsed transaction result:", {
        returnCode: result.returnCode.toString(),
        returnMessage: result.returnMessage || "No message",
      });

      // Check transaction success based on status and return code
      if (
        (transaction.status.isSuccessful() || (allowPending && transaction.status.isPending())) &&
        result.returnCode.isSuccess()
      ) {
        return true;
      }

      // Return error message if available
      if (result.returnMessage) {
        return result.returnMessage;
      }
    } catch (e) {
      console.error("Error parsing transaction results:", e);

      // Check transaction data again
      const txData = transaction.data?.toString().toLowerCase() || "";
      const isEsdtTransaction = txData.includes("esdt");
      const isNutsRouletteTransaction = txData.includes("nuts_roulette");

      // For NutsRoulette or ESDT transactions, prioritize the transaction status 
      // even if parsing fails
      if ((isEsdtTransaction || isNutsRouletteTransaction) && transaction.status.isSuccessful()) {
        console.log("Transaction is successful despite parsing error");
        return true;
      }

      // If we don't need detailed transaction data, just use the transaction status
      if (!returnTransaction) {
        console.log("Using transaction status as fallback");
        return transaction.status.isSuccessful();
      }

      console.log("Transaction parsing failed and returnTransaction is true");
      return false;
    }

    console.log("Transaction status check reached end without conclusive result");
    return false;
  }

  async getPendingTransaction(
    contract: string | null,
    fct: string | null,
    transaction: Transaction,
    returnTransaction: boolean = false,
    ignoreResultItem: boolean = false,
  ): Promise<boolean | string | null | ITransactionOnNetwork> {
    const { default: store, BASE_MUTATIONS } = await import("@/store");

    const hash: string = transaction.getHash().toString();
    const txData = transaction.getData().toString();
    console.log("Checking transaction status for hash:", hash);
    console.log("Transaction data:", txData);

    // Check transaction types
    const isEsdtTransaction = txData.toLowerCase().includes("esdt");
    const isNutsRouletteTransaction = txData.toLowerCase().includes("nuts_roulette");

    // Different games might need different handling
    console.log("Transaction type:", {
      isEsdtTransaction,
      isNutsRouletteTransaction,
    });

    store.commit(BASE_MUTATIONS.TRANSACTION_TOAST, { hash, status: null });

    // Wait for transaction to reach blockchain - longer for complex transactions
    const waitTime = isNutsRouletteTransaction ? 8_000 : 6_000;
    console.log(`Waiting ${ waitTime / 1000 } seconds for transaction to reach blockchain`);
    await new Promise((resolve) => setTimeout(resolve, waitTime));

    // Set longer timeout for complex transactions
    const timeoutMultiplier = isNutsRouletteTransaction ? 3 : 2;
    const transactionWatcher = new TransactionWatcher(elrondApiHelper.apiProvider as unknown as ITransactionFetcher, {
      timeoutMilliseconds: TransactionWatcher.DefaultTimeout * timeoutMultiplier,
    });
    console.log(`Transaction watcher timeout set to ${ TransactionWatcher.DefaultTimeout * timeoutMultiplier }ms`);

    let transactionOnNetwork: any;

    try {
      // For nutsRoulette or ESDT transactions, we can skip the condition check
      // and just await completion
      if (isNutsRouletteTransaction || ignoreResultItem || !returnTransaction) {
        console.log("Using simpler awaitCompleted for transaction");
        transactionOnNetwork = await transactionWatcher.awaitCompleted(transaction);
      } else {
        // For both EGLD and ESDT transactions, use conditional await for faster feedback
        console.log("Using conditional await for transaction");
        transactionOnNetwork = await transactionWatcher.awaitOnCondition(transaction, (transactionOnNetwork) => {
          // For ESDT transactions, check if the transaction status is successful
          if (isEsdtTransaction && transactionOnNetwork.status?.isSuccessful()) {
            console.log("ESDT transaction successful based on status");
            return true;
          }

          const resultItemWithReturnData = transactionOnNetwork.contractResults?.items?.find((item) =>
            item.data?.startsWith("@00@"),
          );

          if (
            !resultItemWithReturnData &&
            true !== this.getTransactionStatus(transactionOnNetwork as unknown as TransactionOnNetwork, true)
          ) {
            return true;
          }

          return (
            resultItemWithReturnData &&
            true === this.getTransactionStatus(transactionOnNetwork as unknown as TransactionOnNetwork, true, true)
          );
        });
      }

      console.log("Transaction successfully processed:", transactionOnNetwork.status?.toString());
    } catch (e) {
      console.error("Error awaiting transaction:", e);

      // Try to get transaction directly from API
      try {
        console.log("Attempting direct transaction fetch");
        transactionOnNetwork = await elrondApiHelper.apiProvider.getTransaction(hash);
        console.log("Retrieved transaction directly:", transactionOnNetwork.status?.toString());
      } catch (fetchError) {
        console.error("Failed to fetch transaction:", fetchError);
        return null;
      }
    }

    if (!hash) {
      console.error("No transaction hash available");
      return null;
    }

    let newTransaction: TransactionOnNetwork = this.transactionCache?.[hash];

    if (!newTransaction) {
      newTransaction = transactionOnNetwork;

      if (!newTransaction) {
        console.error("Could not retrieve transaction");
        return null;
      }

      this.transactionCache[hash] = newTransaction;
    }

    // Check if transaction is for the desired contract and function
    if (contract && fct) {
      try {
        const decoder = new TransactionDecoder();

        const metadata = decoder.getTransactionMetadata({
          sender: newTransaction.sender.bech32(),
          receiver: newTransaction.receiver.bech32(),
          data: newTransaction.data.toString("base64"),
          value: newTransaction.value.toString(),
        });

        if (metadata.receiver !== contract || fct !== metadata.functionName) {
          console.warn("Transaction doesn't match expected contract or function", {
            expectedContract: contract,
            actualReceiver: metadata.receiver,
            expectedFunction: fct,
            actualFunction: metadata.functionName,
          });
          return null;
        }
      } catch (error) {
        console.error("Error decoding transaction metadata:", error);
        // Continue processing even if metadata decoding fails
      }
    }

    // Special handling for complex transactions is already handled in getTransactionStatus now
    // No need for redundant checks here, just get the transaction status
    const status = this.getTransactionStatus(newTransaction, returnTransaction && !ignoreResultItem, returnTransaction);
    console.log("Final transaction status:", status);

    // Update UI toast
    store.commit(BASE_MUTATIONS.TRANSACTION_TOAST, { hash, status });

    if (true === status && returnTransaction) {
      return transactionOnNetwork;
    }

    return status;
  }

  parseCustomResults(transaction: ITransactionOnNetwork, endpoint: EndpointDefinition): any {
    const resultItemWithReturnData = transaction.contractResults.items.find((item) => item.data.startsWith("@00@"));

    if (!resultItemWithReturnData) {
      return null;
    }

    const serializer = new ArgSerializer();

    const values = serializer.stringToValues(resultItemWithReturnData.data.slice(4), endpoint.output);

    return {
      values: values,
      firstValue: values[0],
      secondValue: values[1],
      thirdValue: values[2],
      lastValue: values[values.length - 1],
    };
  }

  decodeLoginToken = (loginToken: string): DecodedLoginTokenType | null => {
    const parts = loginToken.split(".");

    if (parts.length !== 4) {
      return null;
    }

    try {
      const [origin, blockHash, ttl, extraInfo] = parts;
      const parsedExtraInfo = JSON.parse(atob(extraInfo));
      const parsedOrigin = atob(origin);

      return {
        ttl: Number(ttl),
        extraInfo: parsedExtraInfo,
        origin: parsedOrigin,
        blockHash,
      };
    } catch (e) {
      console.error(`Error trying to decode ${ loginToken }:`, e);

      return null;
    }
  };

  decodeNativeAuthToken = (accessToken: string): DecodedNativeAuthTokenType | null => {
    const parts = accessToken.split(".");

    if (parts.length !== 3) {
      console.error(
        "Invalid nativeAuthToken. You may be trying to decode a loginToken. Try using decodeLoginToken method instead",
      );
      return null;
    }

    try {
      const [address, body, signature] = parts;
      const parsedAddress = atob(address);
      const parsedBody = atob(body);
      const parsedInitToken = this.decodeLoginToken(parsedBody);

      if (!parsedInitToken) {
        return {
          address: parsedAddress,
          body: parsedBody,
          signature,
          blockHash: "",
          origin: "",
          ttl: 0,
        };
      }

      const result = {
        ...parsedInitToken,
        address: parsedAddress,
        body: parsedBody,
        signature,
      };

      // if empty object, delete extraInfo
      if (!parsedInitToken.extraInfo?.timestamp) {
        delete result.extraInfo;
      }

      return result;
    } catch (err) {
      return null;
    }
  };
}

const elrondHelper = new ElrondHelper();

export default elrondHelper;
